import React, { useId } from 'react';
import Icone from './Icone';

/**
 * Caixa de marcação do design system (at-69, frente D/D7).
 *
 * 🔴 **Nasce com o consumidor plural mais claro que o kit já teve:** o escopo da
 * D7 sozinho tem **9** `<input type="checkbox">` crus — 6 nas seções do PDF do
 * editor de template, 1 no "Plano ativo", 1 no "Ativa" da motocicleta elétrica e
 * 1 no envio automático por WhatsApp — mais **2** toggles "Mostrar inativos" hoje
 * feitos como botão que troca o próprio rótulo. Fora dele há outros 3 crus em
 * telas já migradas (`RascunhosAdesaoTab`, `FormularioVeiculo`, `ListaProdutos`),
 * que adotam este componente na branch que as tocar.
 *
 * ## A armadilha de cascata que ele vence — e por que não é o `data-ds-campo`
 *
 * `ui/tokens/legado-compartilhado.css` declara **`input[type="checkbox"]` como
 * seletor de ELEMENTO global** (herança de `produto.css`): `appearance: none`,
 * 18×18, borda laranja, e o marcado recebe `::before { content: "✔" }`.
 * Especificidade (0,1,1) — **vence qualquer utilitário `tw-`** (0,1,0), como o
 * `input[type="text"]` que motivou o marcador do `Input`.
 *
 * Aqui a saída **não** é um `:not([data-ds-checkbox])`: aquele arquivo é área de
 * espera e a regra da at-57 é que ele **não recebe regra nova**. O caminho é o
 * mesmo do `RadioCards` — esconder o controle nativo e desenhar a caixa ao lado:
 * o global legado continua aplicando 18×18 e o "✔" **no input invisível**, o que
 * é inócuo, e a aparência visível é toda nossa.
 *
 * 🔴 **O input COBRE o label (`absolute inset-0 opacity-0`), nunca `sr-only`** —
 * a lição do `RadioCards` (bug de 09/09/2026): `sr-only` é `position: absolute`
 * de 1px e, sem `relative` no pai, o containing block vira o **documento**; ao
 * focá-lo no clique o navegador rola o documento inteiro, e o
 * `html, body { overflow: hidden }` de `tokens/base.css` impede o usuário de
 * desfazer isso. Cobrindo o label, o alvo do foco **é** a área do controle e não
 * há para onde rolar.
 *
 * ⚠️ **Nunca `display: none`:** escondido de verdade, o checkbox sai da ordem de
 * foco e deixa de ser alcançável pelo teclado.
 *
 * ⚠️ **`motivoDesabilitado` é texto VISÍVEL, não `title`.** Um controle que não
 * responde precisa dizer por quê, e `title` não existe no celular nem no teclado
 * (at-65) — era o caso do "Listar os produtos linha a linha", cuja única
 * explicação vivia num `title`.
 */

interface PropsCheckbox {
  /** Texto ao lado da caixa. Sempre visível — marcador tem texto (spec 02 §9). */
  rotulo: React.ReactNode;
  /** Linha de apoio abaixo do rótulo, ligada por `aria-describedby`. */
  descricao?: React.ReactNode;
  checked: boolean;
  onChange: (marcado: boolean) => void;
  disabled?: boolean;
  /**
   * Por que o controle está travado. Só aparece com `disabled`, e substitui a
   * `descricao` — quem não pode agir precisa do motivo, não da explicação do que
   * a opção faria.
   */
  motivoDesabilitado?: React.ReactNode;
  /** Peso maior no rótulo — para o checkbox que resume a seção (ex.: "ativo"). */
  destaque?: boolean;
  className?: string;
}

const Checkbox: React.FC<PropsCheckbox> = ({
  rotulo,
  descricao,
  checked,
  onChange,
  disabled = false,
  motivoDesabilitado,
  destaque = false,
  className = '',
}) => {
  const base = useId();
  const idApoio = `${base}-apoio`;

  const apoio = disabled && motivoDesabilitado ? motivoDesabilitado : descricao;

  return (
    <div className={['tw-flex tw-flex-col tw-gap-1', className].filter(Boolean).join(' ')}>
      <label
        /* `relative` é o containing block do input que cobre este label —
           ver o aviso no topo do arquivo. */
        className={[
          'tw-relative tw-flex tw-items-start tw-gap-2.5 tw-m-0 tw-w-auto tw-text-sm',
          disabled ? 'tw-cursor-not-allowed tw-opacity-70' : 'tw-cursor-pointer',
        ].join(' ')}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(evento) => onChange(evento.target.checked)}
          aria-describedby={apoio ? idApoio : undefined}
          /* Invisível, mas OCUPANDO o label inteiro: focável, alternável por
             Espaço e sem deslocamento algum para o navegador rolar até.
             `peer` é o que faz a caixa desenhada reagir ao estado. */
          className="tw-peer tw-absolute tw-inset-0 tw-m-0 tw-h-full tw-w-full tw-cursor-[inherit] tw-appearance-none tw-opacity-0"
        />

        {/* A caixa que se vê. `aria-hidden` porque quem é anunciado é o input. */}
        <span
          aria-hidden="true"
          className={[
            'tw-mt-0.5 tw-flex tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-items-center tw-justify-center',
            'tw-rounded tw-border-2 tw-transition-colors',
            checked
              ? 'tw-border-brand tw-bg-brand tw-text-white'
              : 'tw-border-superficie-borda tw-bg-white tw-text-transparent',
            /* O anel de foco vive na caixa desenhada, porque o input é
               transparente — sem isto, navegar por teclado não mostraria onde se
               está. */
            'peer-focus-visible:tw-ring-2 peer-focus-visible:tw-ring-brand/40',
          ].join(' ')}
        >
          <Icone nome="check" tamanho={12} traco={3} />
        </span>

        <span
          className={[
            'tw-min-w-0',
            destaque
              ? 'tw-font-semibold tw-text-texto-titulo'
              : 'tw-text-texto-corpo',
          ].join(' ')}
        >
          {rotulo}
        </span>
      </label>

      {apoio && (
        <p
          id={idApoio}
          className="tw-m-0 tw-pl-[28px] tw-text-xs tw-leading-relaxed tw-text-texto-desabilitado"
        >
          {apoio}
        </p>
      )}
    </div>
  );
};

export default Checkbox;
